import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

export interface SourceFile {
  id: string;
  name: string;
  thumbUrl?: string;
  fileType?: string;
  parentId?: string | null;
}

export interface InlineSourcesDisplayProps {
  /** Files that were used to generate the response */
  files: SourceFile[];
  /** Additional className */
  className?: string;
  /** Whether to start expanded */
  defaultExpanded?: boolean;
}

/**
 * Inline sources display component (ChatGPT-style)
 * Shows which files were used to generate a response
 * Appears inline alongside action buttons after an assistant message
 */
export const InlineSourcesDisplay: React.FC<InlineSourcesDisplayProps> = ({
  files,
  className,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!files || files.length === 0) return null;

  const headerText =
    files.length === 1
      ? t('chat.sources.usedFile', 'Used 1 file')
      : t('chat.sources.usedFiles', 'Used {{count}} files', {
          count: files.length,
        });

  const handleFileClick = (file: SourceFile) => {
    // Navigate to the file - format: /library/{teamId}/{folderId}/{fileId}
    // We need to construct the URL based on the file's parentId
    const teamId = window.location.pathname.split('/')[2]; // Extract team ID from current URL
    const folderId = file.parentId || 'recents';
    navigate(`/library/${teamId}/${folderId}/${file.id}`);
  };

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className="contents"
    >
      {/* Button on the same line as action buttons */}
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors',
            className,
          )}
        >
          <span>{headerText}</span>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </button>
      </CollapsibleTrigger>

      {/* File list expands below - full width of message container */}
      <CollapsibleContent className="absolute left-0 right-0 top-full overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="flex flex-wrap gap-1.5 mb-4">
          {files.map((file) => {
            const isAudio = file.fileType?.startsWith('audio/');

            return (
              <button
                key={file.id}
                type="button"
                onClick={() => handleFileClick(file)}
                className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity"
              >
                {/* Thumbnail or white background for audio */}
                <div className="w-8 h-6 rounded flex-shrink-0 overflow-hidden bg-white dark:bg-white">
                  {!isAudio && file.thumbUrl && (
                    <img
                      src={file.thumbUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <span className="truncate text-muted-foreground hover:text-foreground max-w-[120px]">
                  {file.name}
                </span>
              </button>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default InlineSourcesDisplay;
